import * as types from '../mutation-types';
import Wavoip from 'wavoip-api';
import { useAlert } from 'dashboard/composables';

const findRecordById = ($state, id) =>
  $state.records.find(record => record.id === Number(id)) || {};

const defaultState = {
  records: [],
  uiFlags: {
    isOpen: false,
    isFetching: false,
    isFetchingItem: false,
    isUpdating: false,
    isCheckoutInProcess: false,
  },
  call: {
    id: null,
    duration: 0,
    tag: null,
    phone: null,
    picture_profile: null,
    status: null,
    direction: null,
    whatsapp_instance: null,
    active_start_date: null,
    chat_id: null,
    inbox_name: null,
    callObject: null,
  },
  wavoip: {},
};

export const getters = {
  getAccount: $state => id => {
    return findRecordById($state, id);
  },
  getUIFlags($state) {
    return $state.uiFlags;
  },
  getCallInfo($state) {
    return $state.call;
  },
  getWavoip($state) {
    return $state.wavoip;
  },
};

export const actions = {
  startWavoip: async ({ commit, state }, { inboxName, token }) => {
    // console.log('WAVOIP Store: Iniciando Wavoip para:', { inboxName, token });

    if (state.wavoip[token] && token) {
      // console.log('WAVOIP Store: Instância já existe para token:', token);
      return;
    }

    try {
      const WAV = new Wavoip();
      const whatsapp_instance = WAV.connect(token);

      commit('ADD_WAVOIP', {
        token: token,
        whatsapp_instance: whatsapp_instance,
        inboxName: inboxName,
      });

      whatsapp_instance.socket.on('connect', () => {
        // console.log('WAVOIP Store: Conectado');
      });

      whatsapp_instance.socket.on('disconnect', () => {
        // console.log('WAVOIP Store: Desconectado');
      });

      // console.log('WAVOIP Store: Instância criada com sucesso');
    } catch (error) {
      // console.error('WAVOIP Store: Erro ao criar instância:', error);
      useAlert(`Erro ao conectar Wavoip: ${error.message}`);
    }
  },

  makeCall: async ({ dispatch }, { token, phone, contact_name, chat_id }) => {
    // console.log('WAVOIP Store: Fazendo chamada:', { token, phone, contact_name });
    try {
      await dispatch('outcomingCall', { token, phone, contact_name, chat_id });
    } catch (error) {
      // console.error('WAVOIP Store: Erro ao fazer chamada:', error);
      useAlert(`Erro ao fazer chamada: ${error.message}`);
      throw error;
    }
  },

  outcomingCall: async ({ commit, state, dispatch }, callInfo) => {
    let { phone, contact_name, chat_id, token } = callInfo;
    // console.log('WAVOIP Store: Iniciando chamada de saída:', callInfo);

    let instances = callInfo.instances ?? Object.keys(state.wavoip);
    let selectedToken = token ?? instances[0];

    if (!selectedToken) {
      throw new Error('Nenhuma instância Wavoip disponível');
    }

    let wavoipInstance = state.wavoip[selectedToken];
    if (!wavoipInstance) {
      throw new Error('Instância Wavoip não encontrada');
    }

    let wavoip = wavoipInstance.whatsapp_instance;
    let inbox_name = wavoipInstance.inbox_name;
    let remainingInstances = instances.filter(instance => instance !== selectedToken);

    if (!wavoip) {
      throw new Error('Instância Wavoip não disponível');
    }

    /*
    console.log('WAVOIP: Verificando métodos disponíveis:', {
      hasStartCall: typeof wavoip.startCall === 'function',
      hasCallStart: typeof wavoip.callStart === 'function',
      availableMethods: Object.getOwnPropertyNames(wavoip).filter(name => typeof wavoip[name] === 'function')
    });
    */

    if (typeof wavoip.startCall === 'function') {
      // console.log('WAVOIP: Usando nova API - startCall');
      
      try {
        wavoip.startCall({
          to: phone,
          onAccept: (call) => {
            // console.log('WAVOIP: Chamada aceita!', call);
            commit('SET_SOFTPHONE_CALL', {
              id: selectedToken,
              duration: 0,
              tag: contact_name,
              phone: phone,
              picture_profile: null,
              status: 'accept',
              direction: 'outcoming',
              whatsapp_instance: selectedToken,
              inbox_name: inbox_name,
              chat_id: chat_id,
              active_start_date: new Date(),
              callObject: call
            });
            dispatch('updateCallStatus', 'accept');
          },
          onReject: () => {
            // console.log('WAVOIP: Chamada rejeitada');
            dispatch('resetCall');
          },
          onEnd: () => {
            // console.log('WAVOIP: Chamada encerrada');
            dispatch('updateCallStatus', 'terminate');
            setTimeout(() => dispatch('resetCall'), 3000);
          },
          onError: (reason) => {
            // console.error('WAVOIP: Erro na chamada:', reason);
            if (remainingInstances.length > 0) {
              return dispatch('outcomingCall', { 
                ...callInfo, 
                instances: remainingInstances, 
                token: null 
              });
            } else {
              dispatch('resetCall');
              throw new Error(`Falha na chamada: ${reason}`);
            }
          }
        });

        commit('SET_SOFTPHONE_CALL', {
          id: selectedToken,
          duration: 0,
          tag: contact_name,
          phone: phone,
          picture_profile: null,
          status: 'outcoming_calling',
          direction: 'outcoming',
          whatsapp_instance: selectedToken,
          inbox_name: inbox_name,
          chat_id: chat_id,
        });

      } catch (error) {
        // console.error('WAVOIP: Erro com nova API:', error);
        throw error;
      }

    } else if (typeof wavoip.callStart === 'function') {
      // console.log('WAVOIP: Usando API antiga - callStart');
      
      let timeout = setTimeout(() => {
        // console.warn('WAVOIP: Timeout da chamada');
        if (remainingInstances.length > 0) {
          dispatch('outcomingCall', { ...callInfo, instances: remainingInstances, token: null });
        }
      }, 10000);

      try {
        const offerResponse = await wavoip.callStart({ whatsappid: phone })
          .then(response => {
            let output;
            if (response.type === 'success') {
              output = { profile_picture: response?.result?.profile_picture };
            } else {
              output = { error: true, message: response?.result };
            }
            return output;
          })
          .catch(response => ({ error: true, message: response?.result }));

        clearTimeout(timeout);

        if (offerResponse.error) {
          // console.error('WAVOIP: Erro na resposta:', offerResponse.message);
          
          if (offerResponse.message === 'Numero não existe') {
            throw new Error(offerResponse.message);
          } else if (offerResponse.message === 'Limite de ligações atingido') {
            throw new Error('Limite de ligações diários atingido');
          }

          if (remainingInstances.length > 0) {
            return dispatch('outcomingCall', { ...callInfo, instances: remainingInstances, token: null });
          } else {
            throw new Error('Linha ocupada, tente mais tarde');
          }
        }

        commit('SET_SOFTPHONE_CALL', {
          id: selectedToken,
          duration: 0,
          tag: contact_name,
          phone: phone,
          picture_profile: offerResponse?.profile_picture,
          status: 'outcoming_calling',
          direction: 'outcoming',
          whatsapp_instance: selectedToken,
          inbox_name: inbox_name,
          chat_id: chat_id,
        });

        // console.log('WAVOIP Store: Chamada iniciada com sucesso via callStart');

      } catch (error) {
        clearTimeout(timeout);
        // console.error('WAVOIP: Erro com API antiga:', error);
        
        if (remainingInstances.length > 0) {
          // console.log('WAVOIP: Tentando próxima instância...');
          return dispatch('outcomingCall', { 
            ...callInfo, 
            instances: remainingInstances, 
            token: null 
          });
        } else {
          throw error;
        }
      }

    } else {
      throw new Error('Nenhuma API de chamada disponível (startCall ou callStart)');
    }

    commit('SET_SOFTPHONE_UI_FLAG', { isOpen: true });
  },

  incomingCall: async ({ commit, state, rootGetters, dispatch }, callInfo) => {
    try {
      // console.log('WAVOIP Store: Chamada recebida:', callInfo);
      const userStatus = rootGetters['getCurrentUserAvailability'] || 'online';

      if (state.call.id || userStatus !== 'online') {
        // console.log('WAVOIP Store: Rejeitando chamada - usuário ocupado ou offline');
        dispatch('rejectCall', callInfo.token);
        return;
      }

      let { phone, contact_name, profile_picture, token } = callInfo;
      let inbox_name = state.wavoip[token]?.inbox_name || 'Desconhecido';

      commit('SET_SOFTPHONE_CALL', {
        id: token,
        duration: 0,
        tag: contact_name,
        phone: phone,
        picture_profile: profile_picture,
        status: 'offer',
        direction: 'incoming',
        whatsapp_instance: token,
        inbox_name: inbox_name,
        chat_id: null,
      });

      commit('SET_SOFTPHONE_UI_FLAG', { isOpen: true });
    } catch (error) {
      // console.error('WAVOIP Store: Erro ao processar chamada recebida:', error);
      throw new Error(error);
    }
  },

  updateCallStatus: ({ commit }, status) => {
    // console.log('WAVOIP Store: Atualizando status da chamada:', status);
    commit('SET_SOFTPHONE_CALL', { status });
    if (status === 'accept') {
      commit('SET_SOFTPHONE_CALL', { active_start_date: new Date() });
    }
  },

  acceptCall: async ({ state, dispatch }) => {
    try {
      // console.log('WAVOIP Store: Aceitando chamada');
      const wavoip_token = state.call.whatsapp_instance;
      const wavoip = state.wavoip[wavoip_token]?.whatsapp_instance;
      if (wavoip) {
        await wavoip.acceptCall();
        dispatch('updateCallStatus', 'accept');
      } else {
        throw new Error('Instância Wavoip não encontrada');
      }
    } catch (error) {
      // console.error('WAVOIP Store: Erro ao aceitar chamada:', error);
      useAlert('Erro ao aceitar chamada');
    }
  },

  rejectCall: async ({ state, dispatch }, token) => {
    try {
      // console.log('WAVOIP Store: Rejeitando chamada');
      const wavoip_token = token ?? state.call.whatsapp_instance;
      const wavoip = state.wavoip[wavoip_token]?.whatsapp_instance;
      if (wavoip) wavoip.rejectCall();
      dispatch('resetCall');
    } catch (error) {
      // console.error('WAVOIP Store: Erro ao rejeitar chamada:', error);
      dispatch('resetCall');
    }
  },

  endCall: async ({ state, dispatch }) => {
    try {
      // console.log('WAVOIP Store: Encerrando chamada');
      if (state.call.callObject && typeof state.call.callObject.end === 'function') {
        // console.log(' WAVOIP: Usando call.end() da nova API');
        await state.call.callObject.end();
      } else {
        // console.log(' WAVOIP: Usando fallback endCall()');
        const wavoip_token = state.call.whatsapp_instance;
        const wavoip = state.wavoip[wavoip_token]?.whatsapp_instance;
        if (wavoip && typeof wavoip.endCall === 'function') {
          wavoip.endCall();
        }
      }
      setTimeout(() => dispatch('resetCall'), 1000);
    } catch (error) {
      // console.error('WAVOIP Store: Erro ao encerrar chamada:', error);
      dispatch('resetCall');
    }
  },

  muteCall: async ({ state }) => {
    try {
      if (state.call.callObject && typeof state.call.callObject.mute === 'function') {
        // console.log(' WAVOIP: Mutando chamada via nova API');
        await state.call.callObject.mute();
      }
    } catch (error) {
      // console.error('WAVOIP Store: Erro ao mutar chamada:', error);
    }
  },

  unmuteCall: async ({ state }) => {
    try {
      if (state.call.callObject && typeof state.call.callObject.unmute === 'function') {
        // console.log('WAVOIP: Desmutando chamada via nova API');
        await state.call.callObject.unmute();
      }
    } catch (error) {
      // console.error('WAVOIP Store: Erro ao desmutar chamada:', error);
    }
  },

  resetCall: async ({ commit }) => {
    // console.log('WAVOIP Store: Resetando chamada');
    commit('SET_SOFTPHONE_CALL', {
      id: null,
      duration: 0,
      tag: null,
      phone: null,
      picture_profile: null,
      status: null,
      direction: null,
      whatsapp_instance: null,
      active_start_date: null,
      inbox_name: null,
      chat_id: null,
      callObject: null,
    });
  },

  // CORRIGIDO: Método renomeado para compatibilidade
  updateSoftphoneVisible: ({ commit }, { isOpen }) => {
    // console.log('WAVOIP Store: Atualizando visibilidade:', isOpen);
    commit('SET_SOFTPHONE_UI_FLAG', { isOpen });
  },
};

export const mutations = {
  SET_SOFTPHONE_UI_FLAG($state, data) {
    $state.uiFlags = { ...$state.uiFlags, ...data };
  },

  ADD_WAVOIP($state, data) {
    $state.wavoip = {
      ...$state.wavoip,
      [data.token]: { whatsapp_instance: data.whatsapp_instance, inbox_name: data.inboxName },
    };
  },

  SET_SOFTPHONE_CALL($state, data) {
    $state.call = { ...$state.call, ...data };
  },
};

export default {
  namespaced: true,
  state: defaultState,
  getters,
  actions,
  mutations,
};